import React, { useContext } from 'react';
import _get from 'lodash/get';
import MappingProps, * as MappingTypes from 'types/Mapping';
import getSourceHeaderCopy from 'components/Mappings/_helpers/getSourceHeaderCopy';
import MappingsContext from 'helpers/MappingsContext';

import Link from '@material-ui/core/Link';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Table from 'components/Table';
import TableHead from 'components/Table/TableHead';
import TableHeadCell from 'components/Table/TableHeadCell';
import TableBody from '@material-ui/core/TableBody';
import MappingsRow from '../MappingsRow';

import { HOME_URL, PERSON_PROFILE_URL } from 'components/Router/consts';
import { PROFILE_ATTR_MAPPING_TYPE_KEY } from 'pages/Directory/consts';

interface Props {
  mappings: MappingProps[];
  onUpdateCard: (newMappings: MappingProps[]) => void;
  onDeleteMapping: (indexToDelete: number) => void;
  onUpdateMapping: (newMappingIndex: number, newMapping: MappingProps) => void;
}

function ProfileAttrMapping({ mappings, onUpdateCard, onDeleteMapping, onUpdateMapping }: Props) {
  const mappingsContext: MappingTypes.MappingContext = useContext(MappingsContext);
  const { source } = mappingsContext;

  const handleAddCustomMapping = () => {
    const newMapping = {
      source: '',
      destination: '',
      isRequired: false,
      isUnique: false,
      type: PROFILE_ATTR_MAPPING_TYPE_KEY,
    };

    const newMappings = [...mappings];
    newMappings.unshift({ ...newMapping, isCustom: true });
    onUpdateCard(newMappings);
  };

  const handleUpdateMapping = (newMappingIndex: number, newMapping: any) => {
    const { destination } = newMapping;

    if (destination === 'firstName' || destination === 'lastName') {
      newMapping['isRequired'] = true;
    }

    if (destination === 'email') {
      newMapping['isUnique'] = true;
    }

    onUpdateMapping(newMappingIndex, newMapping);
  };

  return (
    <>
      <CardHeader
        title={'Profile Attributes'}
        subheader={
          _get(source, 'isPrimary') ? (
            <>
              <>
                Map properties from your data source to your employee{' '}
                <Link
                  href={`${HOME_URL}${PERSON_PROFILE_URL}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="secondary"
                >
                  profile attributes
                </Link>{' '}
                in Sift. For Primary Sources, we require that you supply{' '}
                <strong>first name, last name,</strong> and <strong>email address</strong> for each
                person, but any additional data will make your Sift profiles that much richer! You
                may add, remove, or change these mappings at any time, and you are not required to
                map all properties to Sift.
              </>
              <div style={{ marginTop: 24 }}>
                <strong>Note:</strong> If you already mapped any of these attributes (other than
                email) in your primary or hierarchy mapping section, they do not need to be mapped
                here unless you’d like them to be explicitly displayed on profile pages.
              </div>
            </>
          ) : (
            <>
              Map properties from your data source to your employee profile attributes in Sift. You
              may add, remove, or change these mappings at any time, and you are not required to map
              all properties to Sift.
            </>
          )
        }
        action={
          <Button color="secondary" onClick={handleAddCustomMapping}>
            + Add Mapping
          </Button>
        }
      />
      <CardContent>
        <Table>
          <TableHead>
            <TableHeadCell> {getSourceHeaderCopy(source.type)} </TableHeadCell>
            <TableHeadCell> Preview Data </TableHeadCell>
            <TableHeadCell> Sift Attribute Destination </TableHeadCell>
            <TableHeadCell />
            <TableHeadCell />
          </TableHead>
          <TableBody>
            {mappings.map((mapping, index) => {
              return (
                <MappingsRow
                  key={index}
                  mapping={mapping}
                  rowIndex={index}
                  onDeleteMapping={onDeleteMapping}
                  onUpdateMapping={handleUpdateMapping}
                />
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </>
  );
}

export default ProfileAttrMapping;
